import React, { useContext, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import usePaginatedRequest from 'hooks/usePaginatedRequest';
import api from 'api';
import useQueryParams from 'hooks/useQueryParams';
import Pagination from 'components/Pagination';
import Table from 'components/Table';
import { columns, contentTransformations, headingTranslations } from './data';
import { Select } from 'ncoded-component-library';
import CurrentUserContext from 'providers/CurrentUser';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { Button } from 'ncoded-component-library';
import useCallbackRef from 'hooks/useCallbackRef';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import UploadVehiclesModal from '../../components/UploadVehiclesModal';
import GraphRepresentationVehicles from '../../components/GraphRepresentationVehicles';
import GraphIcon from 'icons/Graph.icon';
import TableIcon from 'icons/Table.icon';

import './Vehicles.styles.scss';
import './Vehicles.styles.responsive.scss';

type VehiclesProps = {
  className?: string;
};

const VEHICLES_LIMIT = '7';
const LIMIT_FLEET = 1000;

const Vehicles: React.FC<VehiclesProps> = (props) => {
  const { className } = props;
  const { t } = useTranslation();
  const { params, setQueryParam, removeQueryParam } = useQueryParams();
  const [fleetFilterOptions, setFleetFilterOptions] = useState([]);
  const { isAdmin } = useContext(CurrentUserContext);
  const { popError } = useContext(PopNotificationsContext);
  const [modal, modalRef] = useCallbackRef<OverlayRef>();

  const request = useMemo(() => api.vehicle.getVehicles, []);

  useEffect(() => {
    if (!isAdmin) return;

    (async () => {
      try {
        const {
          data: { items },
        } = await api.fleet.getFleets({ limit: `${LIMIT_FLEET}` });

        const createSelectOptions = items.map((fleet) => ({
          value: fleet._id,
          label: fleet.name,
        }));
        setFleetFilterOptions([
          { value: '', label: t('none') },
          ...createSelectOptions,
        ]);
      } catch (e: any) {
        popError(e);
      }
    })();
  }, [isAdmin, popError, t]);

  const {
    totalPages,
    isLoading,
    items: vehicles,
    setItems,
  } = usePaginatedRequest({
    getItems: request,
    defaultParams: {
      limit: VEHICLES_LIMIT,
      ...(params.filter ? { filter: JSON.parse(params.filter) } : {}),
    },
    dependencies: [params, request],
  });
  const classes = classNames('vehicles', className);

  const [isTableView, setIsTableView] = useState(true);

  return (
    <div className="vehicles--wrapper">
      <div className={classes}>
        <div className="vehicles__heading">
          <p className="fm-page-title user-title">{t('vehicles')}</p>
          <div className="vehicles__heading__actions">
            {!!fleetFilterOptions.length && (
              <Select
                value={params.filter ? JSON.parse(params.filter).fleetId : ''}
                onChange={(opt: any) => {
                  if (opt.value)
                    setQueryParam(
                      'filter',
                      JSON.stringify({ fleetId: opt.value }),
                    );
                  else {
                    removeQueryParam('filter');
                  }
                }}
                placeholder={t('selectFleet')}
                options={fleetFilterOptions}
              />
            )}
            <Button onClick={() => modal.open()}>{t('upload-vehicles')}</Button>
          </div>
        </div>
        <div className="vehicles__heading">
          <span />
          <div className="vehicles__menu-view">
            <Button
              className={classNames({ selected: isTableView })}
              onClick={() => setIsTableView(true)}
            >
              <TableIcon />
            </Button>
            <Button
              className={classNames({ selected: !isTableView })}
              onClick={() => setIsTableView(false)}
            >
              <GraphIcon />
            </Button>
          </div>
        </div>
        {isTableView ? (
          <Table
            data={vehicles}
            columns={columns}
            headingTranslations={headingTranslations}
            tableContentTransformations={contentTransformations}
            isDataLoading={isLoading}
            updateTableData={setItems}
          />
        ) : (
          <GraphRepresentationVehicles data={vehicles} />
        )}
        <Pagination
          lastPage={+totalPages}
          currentPage={+params.page || 1}
          setPage={(n) => setQueryParam('page', n)}
        />
        <UploadVehiclesModal ref={modalRef} fleetOptions={fleetFilterOptions} />
      </div>
    </div>
  );
};

export default Vehicles;
